import { Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, RefreshCw, Save, RotateCcw, Info } from 'lucide-angular';
import {
  NotificationTemplatesService,
  NotificationTemplateDto,
} from '../../../core/services/notification-templates.service';

/** Bitta xabar, ikki tildagi variantlari bilan. */
interface TemplateGroup {
  key: string;
  description: string;
  tokens: string[];
  rows: NotificationTemplateDto[];
}

@Component({
  selector: 'app-admin-notification-templates',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './notification-templates.component.html',
})
export class AdminNotificationTemplatesComponent implements OnInit {
  private svc = inject(NotificationTemplatesService);
  private readonly destroyRef = inject(DestroyRef);

  RefreshCw = RefreshCw;
  Save = Save;
  RotateCcw = RotateCcw;
  Info = Info;

  templates = signal<NotificationTemplateDto[]>([]);
  loading = signal(false);
  savingKey = signal<string | null>(null);

  /** Tahrir qilinayotgan matnlar: "kalit|til" -> matn. */
  private drafts = signal<Record<string, string>>({});

  groups = computed<TemplateGroup[]>(() => {
    const byKey = new Map<string, TemplateGroup>();
    for (const t of this.templates()) {
      let group = byKey.get(t.key);
      if (!group) {
        group = { key: t.key, description: t.description, tokens: t.tokens, rows: [] };
        byKey.set(t.key, group);
      }
      group.rows.push(t);
    }
    return [...byKey.values()];
  });

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const rows = res.data ?? [];
          this.templates.set(rows);
          // Matn yozilmagan bo'lsa standartni ko'rsatamiz — admin noldan
          // yozmasdan, mavjudini tahrirlab ketaveradi.
          const drafts: Record<string, string> = {};
          for (const r of rows) drafts[this.slot(r)] = r.body ?? r.defaultBody;
          this.drafts.set(drafts);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  slot(row: NotificationTemplateDto): string {
    return `${row.key}|${row.language}`;
  }

  draft(row: NotificationTemplateDto): string {
    return this.drafts()[this.slot(row)] ?? '';
  }

  onDraftChange(row: NotificationTemplateDto, value: string) {
    this.drafts.update((d) => ({ ...d, [this.slot(row)]: value }));
  }

  /** Standartdan farq qiladimi — "Saqlash" tugmasini yoqish uchun. */
  isDirty(row: NotificationTemplateDto): boolean {
    return this.draft(row) !== (row.body ?? row.defaultBody);
  }

  isCustom(row: NotificationTemplateDto): boolean {
    return row.body !== null;
  }

  insertToken(row: NotificationTemplateDto, token: string) {
    this.onDraftChange(row, `${this.draft(row)}${token}`);
  }

  save(row: NotificationTemplateDto) {
    this.savingKey.set(this.slot(row));
    this.svc
      .save({
        key: row.key,
        language: row.language,
        body: this.draft(row),
        isActive: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingKey.set(null);
          this.svc.showSuccess('Matn saqlandi');
          this.load();
        },
        error: (err) => {
          this.savingKey.set(null);
          this.svc.showError(err?.error?.errors?.[0] ?? 'Saqlab bo‘lmadi');
        },
      });
  }

  /** Admin matnini o'chirib, koddagi standartga qaytaradi. */
  resetToDefault(row: NotificationTemplateDto) {
    this.savingKey.set(this.slot(row));
    this.svc
      .save({ key: row.key, language: row.language, body: null, isActive: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingKey.set(null);
          this.svc.showSuccess('Standart matnga qaytarildi');
          this.load();
        },
        error: () => this.savingKey.set(null),
      });
  }

  languageLabel(lang: string): string {
    return lang === 'ru' ? '🇷🇺 Русский' : "🇺🇿 O'zbekcha";
  }
}
